import React, { useContext, useMemo } from "react";
import dayjs from "dayjs";

import CloseIcon from "@mui/icons-material/Close";
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";
import PlaylistPlayOutlinedIcon from "@mui/icons-material/PlaylistPlayOutlined";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";

import { DatabaseContext, UserContext } from "../../context";
import { filePath, shouldPlaylistBeVisible } from "../../utility";

const surfaceSx = {
    borderRadius: 2.5,
    borderColor: "divider",
    overflow: "hidden",
};

function PlaylistRow({ playlist, onSelect }) {
    return (
        <Box
            component="button"
            type="button"
            onClick={onSelect}
            sx={{
                width: "100%",
                p: { xs: 1.5, sm: 2 },
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                border: 0,
                color: "text.primary",
                backgroundColor: "transparent",
                textAlign: "left",
                cursor: "pointer",
                "&:hover": { backgroundColor: "action.hover" },
            }}
        >
            <Box
                sx={{
                    width: 40,
                    height: 40,
                    display: "grid",
                    placeItems: "center",
                    flexShrink: 0,
                    borderRadius: 2,
                    color: "primary.main",
                    backgroundColor: "rgba(0, 149, 61, 0.09)",
                }}
            >
                <PlaylistPlayOutlinedIcon />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                    variant="subtitle2"
                    noWrap
                    sx={{ fontWeight: 800 }}
                >
                    {playlist.name || "Untitled playlist"}
                </Typography>
                <Typography variant="body2" color="text.secondary" noWrap>
                    {playlist.filePath || "File location unavailable"}
                </Typography>
            </Box>
            {playlist.isOwner && (
                <Chip
                    size="small"
                    label="Yours"
                    sx={{
                        backgroundColor: "rgba(0, 149, 61, 0.09)",
                        color: "primary.main",
                    }}
                />
            )}
        </Box>
    );
}

function PlaylistSection({ emptyMessage, icon, playlists, title, onSelect }) {
    return (
        <Paper variant="outlined" sx={surfaceSx}>
            <Box
                sx={{
                    px: 2,
                    py: 1.75,
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    backgroundColor: "background.subtle",
                }}
            >
                {icon}
                <Typography variant="subtitle1" sx={{ flex: 1, fontWeight: 800 }}>
                    {title}
                </Typography>
                <Chip size="small" label={playlists.length} />
            </Box>
            <Divider />
            {playlists.length === 0 ? (
                <Box sx={{ px: 2, py: 3, textAlign: "center" }}>
                    <Typography variant="body2" color="text.secondary">
                        {emptyMessage}
                    </Typography>
                </Box>
            ) : (
                playlists.map((playlist, index) => (
                    <React.Fragment key={playlist.key}>
                        {index > 0 && <Divider />}
                        <PlaylistRow
                            playlist={playlist}
                            onSelect={() => onSelect(playlist)}
                        />
                    </React.Fragment>
                ))
            )}
        </Paper>
    );
}

export default function ProjectSelectPlaylistDialog(props) {
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
    const { currentUserData } = useContext(UserContext);
    const {
        files = [],
        filesLoaded = false,
        folders = [],
        foldersLoaded = false,
        playlistInfo = [],
        playlistInfoLoaded = false,
        playlistUsage = [],
        playlistUsageLoaded = false,
        groups = [],
        groupsLoaded = false,
    } = useContext(DatabaseContext);

    const isLoading =
        !currentUserData ||
        !filesLoaded ||
        !foldersLoaded ||
        !playlistInfoLoaded ||
        !playlistUsageLoaded ||
        !groupsLoaded;

    const { allPlaylists, recentPlaylists } = useMemo(() => {
        if (isLoading) {
            return { allPlaylists: [], recentPlaylists: [] };
        }

        const available = playlistInfo
            .filter((playlist) =>
                shouldPlaylistBeVisible(playlist, groups, currentUserData),
            )
            .map((playlist) => {
                const video = files.find(
                    (file) => file.key === playlist.video,
                );
                const folder = video
                    ? folders.find((item) => item.key === video.folder)
                    : undefined;
                const usage = playlistUsage.find(
                    (item) =>
                        item.user === currentUserData.key &&
                        item.playlist === playlist.key,
                );

                return {
                    ...playlist,
                    isOwner: playlist.owner === currentUserData.key,
                    filePath: video
                        ? filePath(video, folder, folders)
                        : undefined,
                    lastOpened: usage?.lastOpened,
                };
            })
            .sort((a, b) => (a.name || "").localeCompare(b.name || ""));

        return {
            allPlaylists: available,
            recentPlaylists: available
                .filter((playlist) => playlist.lastOpened)
                .sort((a, b) =>
                    dayjs(b.lastOpened).diff(dayjs(a.lastOpened)),
                ),
        };
    }, [
        currentUserData,
        files,
        folders,
        groups,
        isLoading,
        playlistInfo,
        playlistUsage,
    ]);

    return (
        <Dialog
            open={props.open}
            onClose={() => props.onClose()}
            fullScreen={fullScreen}
            fullWidth
            maxWidth="md"
            aria-labelledby="select-playlist-dialog-title"
            slotProps={{
                paper: {
                    sx: {
                        borderRadius: { xs: 0, sm: 3 },
                        overflow: "hidden",
                        maxHeight: { xs: "100%", sm: "90vh" },
                    },
                },
            }}
        >
            <Box
                sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 1.5,
                    px: { xs: 2, sm: 3 },
                    py: 2.5,
                }}
            >
                <Box
                    sx={{
                        width: 42,
                        height: 42,
                        borderRadius: 2,
                        display: "grid",
                        placeItems: "center",
                        flexShrink: 0,
                        color: "primary.main",
                        backgroundColor: "rgba(0, 149, 61, 0.09)",
                    }}
                >
                    <PlaylistPlayOutlinedIcon />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                        id="select-playlist-dialog-title"
                        variant="h5"
                        component="h2"
                        sx={{ lineHeight: 1.1 }}
                    >
                        Add a playlist
                    </Typography>
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mt: 0.5 }}
                    >
                        Choose a playlist to include in this project.
                    </Typography>
                </Box>
                <IconButton
                    aria-label="Close playlist selection"
                    onClick={() => props.onClose()}
                    sx={{ mt: -0.75, mr: -1 }}
                >
                    <CloseIcon />
                </IconButton>
            </Box>

            <DialogContent
                dividers
                sx={{
                    p: { xs: 1.5, sm: 2.5 },
                    backgroundColor: "background.default",
                }}
            >
                {isLoading ? (
                    <Box
                        role="status"
                        sx={{
                            minHeight: 340,
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "text.secondary",
                        }}
                    >
                        <CircularProgress
                            size={32}
                            aria-label="Loading playlists"
                        />
                        <Typography variant="body2" sx={{ mt: 1.5 }}>
                            Loading playlists…
                        </Typography>
                    </Box>
                ) : (
                    <Box sx={{ display: "grid", gap: 2 }}>
                        <PlaylistSection
                            title="Recently opened"
                            emptyMessage="You have not opened any playlists recently."
                            playlists={recentPlaylists}
                            icon={<HistoryOutlinedIcon color="primary" />}
                            onSelect={(playlist) => props.onClose(playlist)}
                        />
                        <PlaylistSection
                            title="All playlists"
                            emptyMessage="No playlists are available."
                            playlists={allPlaylists}
                            icon={<PlaylistPlayOutlinedIcon color="primary" />}
                            onSelect={(playlist) => props.onClose(playlist)}
                        />
                    </Box>
                )}
            </DialogContent>
        </Dialog>
    );
}
